import { createContext, useContext, useState, useCallback, ReactNode } from 'react'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: string
  message: string
  type: ToastType
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void
  showSuccess: (message: string) => void
  showError: (message: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

const toastStyles: Record<ToastType, { bg: string; border: string; icon: string; glow: string }> = {
  success: {
    bg: 'rgba(0, 255, 159, 0.1)',
    border: 'rgba(0, 255, 159, 0.4)',
    icon: '#00FF9F',
    glow: '0 0 20px rgba(0, 255, 159, 0.3)',
  },
  error: {
    bg: 'rgba(255, 64, 64, 0.1)',
    border: 'rgba(255, 64, 64, 0.4)',
    icon: '#FF4040',
    glow: '0 0 20px rgba(255, 64, 64, 0.3)',
  },
  info: {
    bg: 'rgba(0, 240, 255, 0.1)',
    border: 'rgba(0, 240, 255, 0.4)',
    icon: '#00F0FF',
    glow: '0 0 20px rgba(0, 240, 255, 0.3)',
  },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast: Toast = { id, message, type }

    setToasts((prev) => [...prev, newToast])

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 5000)
  }, [])

  const showSuccess = useCallback((message: string) => {
    showToast(message, 'success')
  }, [showToast])

  const showError = useCallback((message: string) => {
    showToast(message, 'error')
  }, [showToast])

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-6 right-6 z-[1001] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => {
          const style = toastStyles[toast.type]
          return (
            <div
              key={toast.id}
              className="pointer-events-auto relative overflow-hidden backdrop-blur-md min-w-[280px] max-w-[400px] animate-[float_0.3s_ease-out]"
              style={{
                background: style.bg,
                border: `1px solid ${style.border}`,
                borderRadius: '4px',
                boxShadow: style.glow,
                animationIterationCount: 1,
              }}
            >
              {/* Top accent line */}
              <div 
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, ${style.icon}, transparent)` }}
              />
              
              {/* Corner accents */}
              <div 
                className="absolute top-0 left-0 w-3 h-3 border-t border-l opacity-60"
                style={{ borderColor: style.icon }}
              />
              <div 
                className="absolute top-0 right-0 w-3 h-3 border-t border-r opacity-60"
                style={{ borderColor: style.icon }}
              />

              <div className="flex items-center gap-3 px-4 py-3">
                {/* Status Indicator */}
                <div className="flex-shrink-0 relative">
                  <div 
                    className="w-2 h-2 rotate-45"
                    style={{ 
                      backgroundColor: style.icon,
                      boxShadow: `0 0 8px ${style.icon}`,
                    }}
                  />
                  <div 
                    className="absolute inset-0 w-2 h-2 rotate-45 animate-ping opacity-50"
                    style={{ backgroundColor: style.icon }}
                  />
                </div>
                
                {/* Message */}
                <span className="flex-1 text-sm font-medium text-[var(--chrome-100)] tracking-wide">
                  {toast.message}
                </span>
                
                {/* Close Button */}
                <button
                  onClick={() => removeToast(toast.id)}
                  className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[var(--chrome-400)] hover:text-[var(--chrome-100)] transition-colors"
                  aria-label="Dismiss notification"
                >
                  <span className="text-lg leading-none">×</span>
                </button>
              </div>

              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black/20">
                <div 
                  className="h-full animate-[shrink_5s_linear_forwards]"
                  style={{ 
                    background: `linear-gradient(90deg, ${style.icon}, transparent)`,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
      
      {/* Add shrink keyframe */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </ToastContext.Provider>
  )
}
